import React, { useState, useEffect } from 'react';
import { useAuth } from '../../AuthContext';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faPlus,
  faEdit,
  faTrash,
  faEye,
  faCheck,
  faTimes,
  faSearch,
  faFilter,
  faArrowLeft,
  faCalendarAlt,
  faFileAlt,
  faCalculator,
  faBalanceScale,
  faSave,
  faUndo
} from "@fortawesome/free-solid-svg-icons";
import './accounting.css';

const JournalEntries = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState('list'); // 'list', 'add', 'edit', 'view'
  const [statistics, setStatistics] = useState({});
  const [accounts, setAccounts] = useState([]);
  
  const { token } = useAuth();
  const { t, i18n } = useTranslation();

  // نموذج إضافة/تعديل القيد
  const [formData, setFormData] = useState({
    entry_date: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    type: 'manual',
    notes: '',
    details: [
      { account_id: '', type: 'debit', amount: 0, description: '', notes: '' },
      { account_id: '', type: 'credit', amount: 0, description: '', notes: '' }
    ]
  });

  useEffect(() => {
    if (currentPage === 'list') {
      fetchEntries();
    }
    if (currentPage === 'add' || currentPage === 'edit') {
      fetchAccounts();
    }
  }, [searchTerm, filterStatus, filterType, currentPage]);

  const fetchAccounts = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/journal-entries/accounts', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      
      if (result.status) {
        setAccounts(result.data);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('type', filterType);

      const response = await fetch(`https://naql.nozzm.com/api/journal-entries?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      
      if (result.status) {
        setEntries(result.data.entries.data);
        setStatistics(result.data.statistics);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('حدث خطأ أثناء جلب البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // التحقق من توازن القيد
    const totalDebit = formData.details
      .filter(d => d.type === 'debit')
      .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    
    const totalCredit = formData.details
      .filter(d => d.type === 'credit')
      .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      setError('القيد غير متوازن. يجب أن يكون إجمالي المدين مساوياً لإجمالي الدائن');
      return;
    }

    try {
      const url = currentPage === 'edit' 
        ? `https://naql.nozzm.com/api/journal-entries/${selectedEntry.id}`
        : 'https://naql.nozzm.com/api/journal-entries';
      
      const method = currentPage === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.status) {
        setCurrentPage('list');
        resetForm();
        fetchEntries();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('حدث خطأ أثناء حفظ البيانات');
    }
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setFormData({
      entry_date: entry.entry_date,
      reference: entry.reference || '',
      description: entry.description || '',
      type: entry.type || 'manual',
      notes: entry.notes || '',
      details: entry.details || [
        { account_id: '', type: 'debit', amount: 0, description: '', notes: '' },
        { account_id: '', type: 'credit', amount: 0, description: '', notes: '' }
      ]
    });
    setCurrentPage('edit');
  };

  const handleView = (entry) => {
    setSelectedEntry(entry);
    setCurrentPage('view');
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا القيد؟')) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/journal-entries/${id}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
          },
        });

        const result = await response.json();
        
        if (result.status) {
          fetchEntries();
        } else {
          setError(result.message);
        }
      } catch (error) {
        setError('حدث خطأ أثناء حذف القيد');
      }
    }
  };

  const handlePost = async (id) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/journal-entries/${id}/post`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
        },
      });

      const result = await response.json();
      
      if (result.status) {
        fetchEntries();
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('حدث خطأ أثناء مراجعة القيد');
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('هل أنت متأكد من إلغاء هذا القيد؟')) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/journal-entries/${id}/cancel`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
          },
        });

        const result = await response.json();
        
        if (result.status) {
          fetchEntries();
        } else {
          setError(result.message);
        }
      } catch (error) {
        setError('حدث خطأ أثناء إلغاء القيد');
      }
    }
  };

  const addDetail = () => {
    setFormData(prev => ({
      ...prev,
      details: [...prev.details, { account_id: '', type: 'debit', amount: 0, description: '', notes: '' }]
    }));
  };

  const removeDetail = (index) => {
    if (formData.details.length > 2) {
      setFormData(prev => ({
        ...prev,
        details: prev.details.filter((_, i) => i !== index)
      }));
    }
  };

  const updateDetail = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      details: prev.details.map((detail, i) => {
        if (i === index) {
          const updatedDetail = { ...detail, [field]: value };
          
          // إذا تم تحديث المبلغ، تأكد من أن النوع صحيح
          if (field === 'amount') {
            // إذا كان المدين له قيمة، اجعل النوع مدين
            if (updatedDetail.type === 'debit' && value > 0) {
              updatedDetail.type = 'debit';
            }
            // إذا كان الدائن له قيمة، اجعل النوع دائن
            else if (updatedDetail.type === 'credit' && value > 0) {
              updatedDetail.type = 'credit';
            }
          }
          
          return updatedDetail;
        }
        return detail;
      })
    }));
  };

  const resetForm = () => {
    setFormData({
      entry_date: new Date().toISOString().split('T')[0],
      reference: '',
      description: '',
      type: 'manual',
      notes: '',
      details: [
        { account_id: '', type: 'debit', amount: 0, description: '', notes: '' },
        { account_id: '', type: 'credit', amount: 0, description: '', notes: '' }
      ]
    });
    setSelectedEntry(null);
  };

  const getStatusText = (status) => {
    const statuses = {
      draft: 'مسودة',
      posted: 'مراجع',
      cancelled: 'ملغي'
    };
    return statuses[status] || status;
  };

  const getStatusClass = (status) => {
    const classes = {
      draft: 'status-draft',
      posted: 'status-posted',
      cancelled: 'status-cancelled'
    };
    return classes[status] || '';
  };

  const getTypeText = (type) => {
    const types = {
      manual: 'يدوي',
      system: 'نظام',
      adjustment: 'تسوية'
    };
    return types[type] || type;
  };

  const calculateTotals = () => {
    const totalDebit = formData.details
      .filter(d => d.type === 'debit')
      .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);
    
    const totalCredit = formData.details
      .filter(d => d.type === 'credit')
      .reduce((sum, d) => sum + parseFloat(d.amount || 0), 0);

    return { 
      totalDebit, 
      totalCredit, 
      isBalanced: Math.abs(totalDebit - totalCredit) < 0.01 
    };
  };

  // Render Add/Edit Page
  const renderAddEditPage = () => {
    const { totalDebit, totalCredit, isBalanced } = calculateTotals();

    return (
      <div className="container tables bg-white mt-5">
        <div className="page-header">
          <button className="back-btn" onClick={() => setCurrentPage('list')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            العودة للقائمة
          </button>
          <div className="page-title">
            <h3>{currentPage === 'edit' ? 'تعديل القيد اليومي' : 'إضافة قيد يومي جديد'}</h3>
            <p>{currentPage === 'edit' ? 'تعديل بيانات القيد اليومي' : 'إنشاء قيد يومي جديد'}</p>
          </div>
        </div>

        <div className="journal-entry-form">
          <form onSubmit={handleSubmit} className="entry-form">
            {/* Main Entry Details Section */}
            <div className="entry-details-section">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">
                    تاريخ القيد *
                  </label>
                  <div className="date-input-wrapper">
                    <input
                      type="date"
                      className="form-control"
                      value={formData.entry_date}
                      onChange={(e) => setFormData({...formData, entry_date: e.target.value})}
                      required
                    />
                    <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    رقم القيد
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={currentPage === 'edit' ? selectedEntry?.entry_number : 'JE-20250823-001'}
                    readOnly
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label className="form-label required">
                    بيان القيد *
                  </label>
                  <textarea
                    className="form-control"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="أدخل بيان القيد...."
                    rows="4"
                    required
                  />
                  {!formData.description && (
                    <div className="error-message">
                      Please fill out this field.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Entry Details Lines Section */}
            <div className="entry-lines-section">
              <div className="section-header">
                <label className="form-label required">
                  تفاصيل القيد *
                </label>
              </div>

              <div className="entry-lines-table">
                <div className="lines-header">
                  <div className="header-cell">اختر الحساب</div>
                  <div className="header-cell">مدين</div>
                  <div className="header-cell">دائن</div>
                  <div className="header-cell">الوصف</div>
                  <div className="header-cell">الإجراءات</div>
                </div>

                {formData.details.map((detail, index) => (
                  <div key={index} className="line-row">
                    <div className="line-cell">
                      <select
                        className="form-control"
                        value={detail.account_id}
                        onChange={(e) => updateDetail(index, 'account_id', e.target.value)}
                        required
                      >
                        <option value="">اختر الحساب</option>
                        {accounts.map(account => (
                          <option key={account.id} value={account.id}>
                            {account.code} - {account.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="line-cell">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={detail.type === 'debit' ? detail.amount : ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          updateDetail(index, 'amount', value);
                          updateDetail(index, 'type', 'debit');
                        }}
                        placeholder="0.00"
                        disabled={detail.type === 'credit' && detail.amount > 0}
                      />
                    </div>
                    <div className="line-cell">
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={detail.type === 'credit' ? detail.amount : ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          updateDetail(index, 'amount', value);
                          updateDetail(index, 'type', 'credit');
                        }}
                        placeholder="0.00"
                        disabled={detail.type === 'debit' && detail.amount > 0}
                      />
                    </div>
                    <div className="line-cell">
                      <input
                        type="text"
                        className="form-control"
                        value={detail.description}
                        onChange={(e) => updateDetail(index, 'description', e.target.value)}
                        placeholder="وصف السطر"
                      />
                    </div>
                    <div className="line-cell">
                      {formData.details.length > 2 && (
                        <button
                          type="button"
                          className="btn-remove-line"
                          onClick={() => removeDetail(index)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="add-line-section">
                <button type="button" className="btn-add-line" onClick={addDetail}>
                  <FontAwesomeIcon icon={faPlus} />
                  + إضافة سطر جديد
                </button>
              </div>
            </div>

            {/* Summary Totals Section */}
            <div className="totals-section">
              <div className="totals-container">
                <div className="total-box credit">
                  <span className="total-label">إجمالي الدائن:</span>
                  <span className="total-amount">{totalCredit.toFixed(2)}</span>
                </div>
                <div className="total-box debit">
                  <span className="total-label">إجمالي المدين:</span>
                  <span className="total-amount">{totalDebit.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button type="submit" className="btn-save" disabled={!isBalanced}>
                <FontAwesomeIcon icon={faSave} />
                حفظ القيد
              </button>
              <button 
                type="button" 
                className="btn-cancel"
                onClick={() => setCurrentPage('list')}
              >
                إلغاء
              </button>
            </div>
          </form>
        </div>

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render View Page
  const renderViewPage = () => {
    if (!selectedEntry) return null;

    return (
      <div className="container tables bg-white mt-5">
        <div className="page-header">
          <button className="back-btn" onClick={() => setCurrentPage('list')}>
            <FontAwesomeIcon icon={faArrowLeft} />
            العودة للقائمة
          </button>
          <div className="page-title">
            <h3>عرض القيد اليومي</h3>
            <p>تفاصيل القيد رقم: {selectedEntry.entry_number}</p>
          </div>
        </div>

        <div className="entry-details">
          <div className="entry-header">
            <div className="entry-info">
              <div className="info-item">
                <label>رقم القيد:</label>
                <span>{selectedEntry.entry_number}</span>
              </div>
              <div className="info-item">
                <label>التاريخ:</label>
                <span>{selectedEntry.entry_date}</span>
              </div>
              <div className="info-item">
                <label>الحالة:</label>
                <span className={getStatusClass(selectedEntry.status)}>
                  {getStatusText(selectedEntry.status)}
                </span>
              </div>
              <div className="info-item">
                <label>النوع:</label>
                <span>{getTypeText(selectedEntry.type)}</span>
              </div>
            </div>
            <div className="entry-actions">
              {selectedEntry.status === 'draft' && (
                <>
                  <button
                    className="btn-post"
                    onClick={() => handlePost(selectedEntry.id)}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                    مراجعة
                  </button>
                  <button
                    className="btn-edit"
                    onClick={() => handleEdit(selectedEntry)}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    تعديل
                  </button>
                </>
              )}
              {selectedEntry.status !== 'cancelled' && (
                <button
                  className="btn-cancel-entry"
                  onClick={() => handleCancel(selectedEntry.id)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  إلغاء
                </button>
              )}
            </div>
          </div>

          <div className="entry-content">
            <div className="entry-description">
              <h5>وصف القيد</h5>
              <p>{selectedEntry.description}</p>
            </div>

            <div className="entry-details-table">
              <h5>تفاصيل القيد</h5>
              <table className="table">
                <thead>
                  <tr>
                    <th>الحساب</th>
                    <th>النوع</th>
                    <th>المبلغ</th>
                    <th>الوصف</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEntry.details?.map((detail, index) => (
                    <tr key={index}>
                      <td>{detail.account?.name}</td>
                      <td>{detail.type === 'debit' ? 'مدين' : 'دائن'}</td>
                      <td>{detail.amount}</td>
                      <td>{detail.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="entry-totals">
              <div className="total-item">
                <span>إجمالي المدين:</span>
                <span className="amount">{selectedEntry.total_debit}</span>
              </div>
              <div className="total-item">
                <span>إجمالي الدائن:</span>
                <span className="amount">{selectedEntry.total_credit}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render List Page
  const renderListPage = () => {
    if (loading) {
      return (
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>جاري تحميل القيود اليومية...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="container tables bg-white mt-5">
        <div className="tableTitle d-flex justify-content-between align-items-center">
          <h3>القيود اليومية</h3>
          <div className="AddServicesButton">
            <button onClick={() => setCurrentPage('add')}>
              <FontAwesomeIcon icon={faPlus} />
              إضافة قيد جديد
            </button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="filters-section">
          <div className="row">
            <div className="col-md-4">
              <div className="search-box">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="البحث في القيود..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <div className="filter-box">
                <FontAwesomeIcon icon={faFilter} className="filter-icon" />
                <select
                  className="form-control"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">جميع الحالات</option>
                  <option value="draft">مسودة</option>
                  <option value="posted">مراجع</option>
                  <option value="cancelled">ملغي</option>
                </select>
              </div>
            </div>
            <div className="col-md-3">
              <select
                className="form-control"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">جميع الأنواع</option>
                <option value="manual">يدوي</option>
                <option value="system">نظام</option>
                <option value="adjustment">تسوية</option>
              </select>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faFileAlt} />
            </div>
            <div className="stat-content">
              <h5>إجمالي القيود</h5>
              <span className="stat-number">{statistics.total_entries || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faCheck} />
            </div>
            <div className="stat-content">
              <h5>القيود المراجعة</h5>
              <span className="stat-number">{statistics.posted_entries || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faEdit} />
            </div>
            <div className="stat-content">
              <h5>القيود المسودة</h5>
              <span className="stat-number">{statistics.draft_entries || 0}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faTimes} />
            </div>
            <div className="stat-content">
              <h5>القيود الملغية</h5>
              <span className="stat-number">{statistics.cancelled_entries || 0}</span>
            </div>
          </div>
        </div>

        {/* Entries Table */}
        <div className="table-responsive">
          <table className="table borderless">
            <thead>
              <tr>
                <th scope="col">رقم القيد</th>
                <th scope="col">التاريخ</th>
                <th scope="col">الوصف</th>
                <th scope="col">النوع</th>
                <th scope="col">الحالة</th>
                <th scope="col">إجمالي المدين</th>
                <th scope="col">إجمالي الدائن</th>
                <th scope="col">الإعدادات</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.entry_number}</td>
                  <td>{entry.entry_date}</td>
                  <td>{entry.description}</td>
                  <td>{getTypeText(entry.type)}</td>
                  <td className={getStatusClass(entry.status)}>
                    {getStatusText(entry.status)}
                  </td>
                  <td>{entry.total_debit}</td>
                  <td>{entry.total_credit}</td>
                  <td>
                    <div className="icons">
                      <button
                        className="editbutton"
                        onClick={() => handleView(entry)}
                        title="عرض"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      {entry.status === 'draft' && (
                        <>
                          <button
                            className="editbutton"
                            onClick={() => handleEdit(entry)}
                            title="تعديل"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            className="editbutton"
                            onClick={() => handlePost(entry.id)}
                            title="مراجعة"
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                        </>
                      )}
                      {entry.status !== 'cancelled' && (
                        <button
                          className="editbutton"
                          onClick={() => handleCancel(entry.id)}
                          title="إلغاء"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                      {entry.status === 'draft' && (
                        <button
                          className="editbutton"
                          onClick={() => handleDelete(entry.id)}
                          title="حذف"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && (
          <div className="alert alert-danger mt-3">
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render based on current page
  if (currentPage === 'add' || currentPage === 'edit') {
    return renderAddEditPage();
  }

  if (currentPage === 'view') {
    return renderViewPage();
  }

  return renderListPage();
};

export default JournalEntries;
